<template>
  <div :class="containerClass">
    <page-header :title="$route.params.id ? 'Edit Interview' : 'Post Interview'" :container-class="null" />

    <div class="page-section">
      <div class="card">
        <form-wizard
          title=""
          subtitle=""
          @on-complete="onComplete"
          shape="tab"
          :color="APP_DARK_COLOR"
          ref="jobForm"
          class="tab-fwizard"
        >
          <tab-content title="General" icon="fas fa-info-circle" :before-change="() => onNext('basicInfoForm')">
            <simulation-basic-form :simulationData="simulation" ref="basicInfoForm"></simulation-basic-form>
          </tab-content>
          <tab-content :title="'Target Audience'" icon="fas fa-user" :before-change="() => onNext('audienceForm')">
            <simulation-audience-form
              :is-org-data="isOrg"
              :simulationData="simulation"
              :are-employers-loading="areEmployersLoading"
              :all-employers="allEmployers"
              :all-programs="allPrograms"
              :are-programs-loading="areProgramsLoading"
              :are-orgs-loading="areOrgsLoading"
              :all-orgs="allOrgs"
              :are-schools-loading="areSchoolsLoading"
              :all-schools="allSchools"
              @orgChanged="changeSelectedOrg"
              @employerChanged="changeSelectedEmployer"
              @orgsFetched="fetchAllOrgs"
              @schoolChanged="changeSelectedSchool"
              ref="audienceForm"
            ></simulation-audience-form>
          </tab-content>

          <tab-content
            :title="'Questions'"
            icon="fas fa-hand-holding-medical"
            :before-change="() => onNext('questionsForm')"
          >
            <simulation-questions-form :simulationData="simulation" ref="questionsForm"></simulation-questions-form>
          </tab-content>

          <tab-content
            title="Conversation Style"
            icon="fas fa-graduation-cap"
            :before-change="() => onNext('conversationForm')"
          >
            <simulation-conversation-form
              :simulationData="simulation"
              ref="conversationForm"
            ></simulation-conversation-form>
          </tab-content>

          <tab-content title="Feedback" icon="fas fa-comments" :before-change="() => onNext('feedbackForm')">
            <simulation-feedback-form :simulationData="simulation" ref="feedbackForm"></simulation-feedback-form>
          </tab-content>
          <template v-slot:prev>
            <button class="btn btn-dark btn-normal" type="dark" :disabled="isFormLoading" style="min-width: 120px">
              Back
            </button>
          </template>

          <template v-slot:next>
            <b-button
              type="submit"
              variant="dark"
              :disabled="isFormLoading"
              style="min-width: 120px"
              class="btn-normal"
            >
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>Next</span>
            </b-button>
          </template>

          <template v-slot:finish>
            <button
              class="btn btn-primary btn-normal"
              type="primary"
              :disabled="isFormLoading"
              style="min-width: 120px"
            >
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>{{ $route.params.id ? 'Update' : 'Post Interview' }}</span>
            </button>
          </template>

          <template #step="props" v-if="isFormLoading">
            <wizard-step :tab="props.tab" :transition="props.transition" :index="props.index" />
          </template>
        </form-wizard>
      </div>
    </div>
  </div>
</template>

<script>
import { get, pick } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import Page from '../../../components/Page.vue';
import PageHeader from '../../../components/Ui/PageHeader.vue';
import SimulationBasicForm from './SimulationBasicForm.vue';
import SimulationQuestionsForm from './SimulationQuestionsForm.vue';
import SimulationConversationForm from './SimulationConversationForm.vue';
import SimulationAudienceForm from './SimulationAudienceForm.vue';
import { ANAM_DEFAULT_AVATAR_ID, APP_DARK_COLOR, USER_ROLES } from '../../../common/constants';
import SimulationFeedbackForm from './SimulationFeedbackForm.vue';

export default {
  components: {
    PageHeader,
    SimulationBasicForm,
    SimulationQuestionsForm,
    SimulationConversationForm,
    SimulationAudienceForm,
    SimulationFeedbackForm,
  },
  extends: Page,

  data() {
    return {
      APP_DARK_COLOR,
      title: this.$route.params.id ? 'Edit Interview' : 'Post Interview',
      isOrg: false,
      simulation: {
        simulation_type: '',
        title: '',
        image: '',
        instructions: '',
        details: '',
        schools: null,
        courses: [],
        employer: null,
        org: null,
        associated_programs: null,
        questionFlow: null,
        questions: '',
        voice: '',
        closing: '',
        greetings: '',
        personality: '',
        provider: 'retell',
        avatar: ANAM_DEFAULT_AVATAR_ID,
        llmId: '',
        is_featured: false,
        universities: [],
        requireFeedback: true,
        rubricType: '',
        rubric: '',
        simulation_purpose: 'employment',
        video_url: '',
      },
      allEmployers: [],
      // The schools and programs this interview is already published for, kept
      // aside so the pickers can label them -- see restoreSelectedOptions().
      postedSchools: [],
      postedPrograms: [],
      allSchools: [],
      allPrograms: [],
      allOrgs: [],
      areOrgsLoading: false,
      isLoading: false,
      areEmployersLoading: false,
      areSchoolsLoading: false,
      areProgramsLoading: false,
      USER_ROLES,
    };
  },

  watch: {
    'simulation.simulation_purpose'(newPurpose, oldPurpose) {
      if (newPurpose === oldPurpose) return;
      if (newPurpose === 'school_scenario') {
        this.isOrg = false;
        this.simulation.employer = null;
        this.simulation.org = null;
        this.simulation.universities = [];
        // The picker flips to multi-select, so normalise whatever is held now.
        this.simulation.schools = this.selectedSchoolIds;
        this.fetchSchools();
      } else {
        this.simulation.courses = [];
      }
    },
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isFormLoading() {
      return this.isLoading || this.areProgramsLoading || this.areEmployersLoading || this.areSchoolsLoading;
    },
    isSchoolScenario() {
      return this.simulation.simulation_purpose === 'school_scenario';
    },
    selectedSchoolIds() {
      const schools = this.simulation.schools;
      if (!schools) return [];
      return Array.isArray(schools) ? schools : [schools];
    },
    schoolScenarioSchoolId() {
      // A scenario is published for several schools but still owned by one —
      // the first selected school is its owner.
      return this.selectedSchoolIds[0] || null;
    },
  },

  methods: {
    ...mapActions('simulations', ['getSimulation', 'createSimulation', 'updateSimulation']),
    ...mapActions('employer', ['getAllEmployers']),
    ...mapActions('partnership', ['getAllPartneredSchools']),
    ...mapActions('program', ['getPublicSchoolPrograms', 'getAllPrograms']),
    ...mapActions('organization', ['getAllOrganizations']),
    ...mapActions('school', ['getAllSchools']),
    async fetchPrograms() {
      this.areProgramsLoading = true;

      try {
        if (this.isOrg) {
          const response = await this.getAllPrograms({ ordering: 'id' });
          this.allPrograms = response.data;

          if (!this.allPrograms.length) {
            this.allPrograms.push({ title: 'Other', id: 'other' });
          }
        } else {
          const response = await this.getPublicSchoolPrograms({ school: this.simulation.schools });
          this.allPrograms = response.data;

          if (!this.allPrograms.length) {
            this.allPrograms.push({ title: 'Other', id: 'other' });
          }
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    fetchAllOrgs(value) {
      this.simulation.org = null;
      this.simulation.employer = null;
      this.simulation.schools = null;
      this.simulation.associated_programs = [];
      this.allSchools = [];
      this.allEmployers = [];
      if (value) {
        this.isOrg = true;
        this.fetchOrgs();
        this.fetchSchools();
        this.fetchPrograms();
      } else {
        this.isOrg = false;
        this.fetchEmployers();
      }
    },
    changeSelectedOrg(value) {
      if (value) {
        this.simulation.org = value;
        this.fetchEmployers(value);
      } else {
        this.simulation.org = null;
        this.allSchools = [];
        this.allEmployers = [];
        this.allPrograms = [];
      }
    },
    async fetchOrgs() {
      this.areOrgsLoading = true;
      try {
        const response = await this.getAllOrganizations({});
        this.allOrgs = response.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.areOrgsLoading = false;
    },

    onNext(formRef) {
      let dataValidation = { isValid: false, formData: {} };
      if (this.$refs[formRef]) {
        dataValidation = this.$refs[formRef].validateFormData();
        this.simulation = { ...this.simulation, ...dataValidation.formData };
      }
      if (!dataValidation.isValid) {
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      }
      return dataValidation.isValid;
    },

    async onComplete() {
      this.isLoading = true;

      try {
        const data = {
          title: this.simulation.title,
          is_featured: this.simulation.is_featured,
          image_url: this.simulation.image,
          instructions: this.simulation.instructions,
          details: this.simulation.details,
          // School scenarios are owned by the school they are published for;
          // every other type keeps the employer / organization owner.
          posted_by_id: this.isSchoolScenario
            ? this.schoolScenarioSchoolId
            : this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN
            ? this.isOrg
              ? this.simulation.org
              : this.simulation.employer
            : this.getLoggedInUser.linked_entity.id,
          posted_by_type: this.isSchoolScenario
            ? 'school'
            : this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN
            ? this.isOrg
              ? 'organization'
              : 'employer'
            : this.getLoggedInUser.role === USER_ROLES.EMPLOYER_ADMIN
            ? 'employer'
            : 'organization',
          question_flow: this.simulation.questionFlow,
          questions: this.simulation.questions,
          required_feedback: this.simulation.requireFeedback,
          rubric_type: this.simulation.requireFeedback ? this.simulation.rubricType : '',
          rubric: this.simulation.requireFeedback ? this.simulation.rubric : '',
          simulation_purpose: this.simulation.simulation_purpose,
          video_url: this.simulation.video_url,
          personality_type: this.simulation.personality,
          greeting: this.simulation.greetings,
          closing: this.simulation.closing,
          type: this.simulation.simulation_type,
          courses: this.isSchoolScenario ? this.simulation.courses || [] : [],
          voice_id: this.simulation.voice,
          // Anam only -- the persona's face. Retell ignores it.
          avatar_id: this.simulation.avatar,
          // Anam only, and dropped by the serializer for every role but super
          // admin. Unlike provider this IS read on edit: an author can move an
          // existing interview onto another model.
          llm_id: this.simulation.llmId,
          // Only read on create -- an existing interview keeps the agent, and
          // therefore the provider, it was created with.
          provider: this.simulation.provider,
          employers: this.isSchoolScenario || !this.simulation.employer
            ? []
            : (this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN && this.isOrg) ||
              this.getLoggedInUser.role === USER_ROLES.ORGANIZATION
            ? typeof this.simulation.employer === 'number'
              ? [this.simulation.employer]
              : this.simulation.employer
            : [],
          schools: this.selectedSchoolIds,
          programs:
            get(this.simulation.associated_programs[0], 'id') === 'other' ||
            this.simulation.associated_programs === 'other'
              ? []
              : this.simulation.associated_programs,

          universities: this.isSchoolScenario ? [] : this.simulation.universities,
        };

        if (this.$route.params.id) {
          await this.updateSimulation({
            id: this.$route.params.id,
            data,
          });
          this.makeToast({ variant: 'success', msg: 'Interviews Updated' });
        } else {
          await this.createSimulation(data);
          this.makeToast({ variant: 'success', msg: 'Interviews Posted' });
        }
        setTimeout(() => this.$router.push({ name: 'simulations-management-list' }), 250);
      } catch (err) {
        // The provider's own reason (e.g. "Voice not found") is far more
        // actionable than the generic message.
        const detail = get(err, 'response.data.data') || get(err, 'response.data.message');
        this.makeToast({
          variant: 'danger',
          msg: typeof detail === 'string' && detail ? detail : this.$t('generalMsgs.genErrorMsg'),
        });
      }

      this.isLoading = false;
    },

    changeSelectedEmployer(employer_id) {
      if (!this.isOrg) {
        this.simulation.schools = null;
        this.allSchools = [];
        if (employer_id) {
          this.fetchSchools(employer_id);
        } else {
          this.simulation.employer = null;
        }
      }
    },
    async changeSelectedSchool(obj) {
      if (this.isSchoolScenario) {
        // Multi-select: the program list is the union across the picked schools.
        const schoolIds = Array.isArray(obj.value) ? obj.value : obj.value ? [obj.value] : [];
        this.simulation.employer = null;
        this.simulation.schools = schoolIds;
        this.simulation.associated_programs = [];
        this.allPrograms = [];
        if (schoolIds.length) {
          this.areProgramsLoading = true;
          try {
            const responses = await Promise.all(
              schoolIds.map(school => this.getPublicSchoolPrograms({ school }))
            );
            const seen = new Set();
            this.allPrograms = responses
              .flatMap(response => response.data)
              .filter(program => !seen.has(program.id) && seen.add(program.id));
          } catch (e) {
            this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
          }
          this.areProgramsLoading = false;
        }
        return;
      }
      if (!this.isOrg) {
        if (obj.value) {
          this.simulation.employer = obj.employer;
          this.simulation.schools = obj.value;
          this.simulation.associated_programs = obj.associated_programs;
          this.areProgramsLoading = true;
          const school = this.allSchools.find(school => school.id === obj.value);
          this.allPrograms = get(school, 'partnership_programs', []);

          if (!this.allPrograms.length) {
            this.allPrograms.push({ title: 'Other', id: 'other' });
          }

          this.areProgramsLoading = false;
        } else {
          this.simulation.associated_programs = [];
          this.allPrograms = [];
          this.simulation.schools = null;
        }
      }
    },

    async fetchEmployers(org_id = null) {
      this.areEmployersLoading = true;
      let resp;
      if (org_id) {
        resp = await this.getAllEmployers({
          organization__id: org_id,
        });
      } else {
        resp = await this.getAllEmployers({});
      }
      this.allEmployers = resp.data;

      this.areEmployersLoading = false;
    },
    async fetchSchools(employer_id = null) {
      this.areSchoolsLoading = true;
      this.allSchools = [];
      if (this.isSchoolScenario) {
        // School scenarios have no employer, so every school is selectable.
        const response = await this.getAllSchools({});

        this.allSchools = response.data;
      } else if (this.isOrg) {
        const response = await this.getAllSchools({});

        this.allSchools = response.data;
      } else if (!this.isOrg) {
        const response = await this.getAllPartneredSchools({ employer: employer_id });

        this.allSchools = response.data;
      } else {
        const response = await this.getAllPartneredSchools({});

        this.allSchools = response.data;
      }
      this.areSchoolsLoading = false;
    },
    // The school and program lookups are role-scoped -- an organization, for
    // one, only gets profile-visible schools -- so what this interview is
    // already published for can be absent from the options, and v-select then
    // renders the raw id instead of the name. Add back whatever is missing.
    restoreSelectedOptions() {
      const missing = (options, posted) => posted.filter(item => !options.some(option => option.id === item.id));

      this.allSchools = [...this.allSchools, ...missing(this.allSchools, this.postedSchools)];
      this.allPrograms = [...this.allPrograms, ...missing(this.allPrograms, this.postedPrograms)];
    },
    async fetchSimulation() {
      this.isLoading = true;

      try {
        const resp = (await this.getSimulation(this.$route.params.id)).data;
        this.simulation = {
          ...pick(resp, ['title', 'instructions', 'details', 'questions', 'closing', 'is_featured']),
        };

        this.simulation.image = get(resp, 'image_url');
        this.simulation.questionFlow = get(resp, 'question_flow');
        this.simulation.personality = get(resp, 'personality_type');
        this.simulation.greetings = get(resp, 'greeting');
        this.simulation.simulation_type = get(resp, 'type');
        this.simulation.voice = get(resp, 'agent.voice_id');
        this.simulation.provider = get(resp, 'agent.provider', 'retell');
        this.simulation.avatar = get(resp, 'agent.anam_avatar_id') || ANAM_DEFAULT_AVATAR_ID;
        // The id the persona was actually built with, so the picker shows the
        // model this interview is running rather than today's default.
        this.simulation.llmId = get(resp, 'agent.anam_llm_id') || '';
        this.simulation.requireFeedback = get(resp, 'required_feedback');
        this.simulation.rubricType = get(resp, 'rubric_type');
        this.simulation.rubric = get(resp, 'rubric');
        this.simulation.simulation_purpose = get(resp, 'simulation_purpose');
        this.simulation.video_url = get(resp, 'video_url');

        const isSchoolScenario = this.simulation.simulation_purpose === 'school_scenario';
        this.simulation.schools =
          isSchoolScenario || resp.posted_by.entity_type === 'organization'
            ? resp.schools.map(school => school.id)
            : get(resp.schools, '[0].id');
        this.simulation.courses = (get(resp, 'courses') || []).map(course =>
          typeof course === 'object' ? course.id : course
        );
        this.simulation.associated_programs = resp.programs.length
          ? resp.programs.map(program => program.id)
          : [{ title: 'Other', id: 'other' }];
        this.postedSchools = get(resp, 'schools') || [];
        this.postedPrograms = resp.programs || [];

        this.simulation.employer = isSchoolScenario
          ? null
          : resp.posted_by.entity_type === 'organization'
          ? resp.employers.map(emp => emp.id)
          : get(resp, 'posted_by_id');

        this.simulation.org =
          !isSchoolScenario && resp.posted_by.entity_type === 'organization' ? get(resp, 'posted_by_id') : null;
        this.isOrg = !isSchoolScenario && resp.posted_by.entity_type === 'organization';

        this.simulation.universities = resp.universities;

        this.areProgramsLoading = true;

        if (this.selectedSchoolIds.length || this.isOrg) {
          await this.fetchSchools(this.simulation.employer);
          if (isSchoolScenario) {
            // Scenarios span several schools; offer the union of their programs.
            const responses = await Promise.all(
              this.selectedSchoolIds.map(school => this.getPublicSchoolPrograms({ school }))
            );
            const seen = new Set();
            this.allPrograms = responses
              .flatMap(response => response.data)
              .filter(program => !seen.has(program.id) && seen.add(program.id));
          } else if (!this.isOrg) {
            const school = this.allSchools.find(school => school.id === this.simulation.schools);
            this.allPrograms = get(school, 'partnership_programs', []);
          }
        }
        this.areProgramsLoading = false;
      } catch (e) {
        if (this.$route.name === 'edit-simulation') {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    if (this.getLoggedInUser.role === USER_ROLES.EMPLOYER_ADMIN) {
      this.fetchSchools();
    }

    if (this.$route.params.id) {
      await this.fetchSimulation();
    }
    if (this.simulation.org) {
      this.fetchOrgs();
    }

    if (this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN) {
      this.fetchEmployers(this.simulation.org);
      await this.fetchPrograms();
    } else if (this.getLoggedInUser.role === USER_ROLES.ORGANIZATION) {
      this.isOrg = true;
      this.fetchEmployers(this.simulation.org);
      // On an edit fetchSimulation() has already loaded them.
      if (!this.$route.params.id) this.fetchSchools();
      await this.fetchPrograms();
    }

    if (this.$route.params.id) {
      this.restoreSelectedOptions();
    }
  },
};
</script>
